import { capitalize, snakeCase, startCase } from 'lodash';
import type { ReactNode } from 'react';

import type {
  AtlasGqlPictureInferenceFilterInput,
  AtlasGqlPictureInferenceFilterOption,
  AtlasGqlPictureInferenceRangeFilterOption,
} from 'types/atlas-graphql';

type DetectionType = AtlasGqlPictureInferenceFilterOption;
type RangeFilter = AtlasGqlPictureInferenceRangeFilterOption;

type FilterOptionsGroupValue = {
  deltaTRange?: RangeFilter | null;
  detectionTypes?: Array<DetectionType | null> | null;
  inferenceApplication?: string | null;
} | null;

const pictureInferenceFilterOptionGroupKeys = ['panelDetectionMeta'] as const;

type PictureInferenceFilterOptionGroupKey = (typeof pictureInferenceFilterOptionGroupKeys)[number];

export type PictureInferenceFilterOptionsValue = Partial<
  Record<PictureInferenceFilterOptionGroupKey, FilterOptionsGroupValue>
>;

export const DELTA_T_INCOMPATIBLE_DETECTION_TYPE_IDS = ['none', 'no_modules'];

export const hasDeltaTIncompatibleDetectionType = (selectedDetectionTypeIds: string[] = []) =>
  selectedDetectionTypeIds.some(id => DELTA_T_INCOMPATIBLE_DETECTION_TYPE_IDS.includes(id));

export const fallbackInferenceApplicationFromKey = (groupKey: string) => snakeCase(groupKey);

export const formatInferenceApplicationLabel = (value: string) => {
  const normalizedValue = startCase(value).trim().toLowerCase();

  if (!normalizedValue) {
    return value;
  }

  return capitalize(normalizedValue);
};

export type PictureInferenceDetectionTypeOption = {
  description?: string;
  label: string;
  value: string;
};

export type PictureInferenceDetectionTypeSelectOption = Omit<
  PictureInferenceDetectionTypeOption,
  'label'
> & {
  label: ReactNode;
  searchText: string;
};

export type PictureInferenceFilterOptionGroup = {
  deltaTRange?: RangeFilter;
  inferenceApplication: string;
  options: PictureInferenceDetectionTypeOption[];
};

export type PictureInferenceFilterSelectOptionGroup = {
  label: string;
  options: PictureInferenceDetectionTypeSelectOption[];
};

const buildPictureInferenceFilterOptionGroup = (
  groupKey: PictureInferenceFilterOptionGroupKey,
  group: FilterOptionsGroupValue
): PictureInferenceFilterOptionGroup | undefined => {
  if (!group) {
    return undefined;
  }

  const options = buildPictureInferenceDetectionTypeOptions(group.detectionTypes ?? []);

  if (!options.length && !group.deltaTRange) {
    return undefined;
  }

  return {
    deltaTRange: group.deltaTRange ?? undefined,
    inferenceApplication: formatInferenceApplicationLabel(
      group.inferenceApplication ?? fallbackInferenceApplicationFromKey(groupKey)
    ),
    options,
  };
};

export const buildPictureInferenceDetectionTypeOptions = (
  detectionTypes: Array<DetectionType | null> = []
): PictureInferenceDetectionTypeOption[] => {
  return [...detectionTypes]
    .filter((option): option is DetectionType => Boolean(option?.id && option?.label))
    .map(option => ({
      description: option.description ?? undefined,
      label: option.label,
      value: option.id,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
    );
};

export const filterSelectedPictureInferenceDetectionTypeIds = (
  selectedDetectionTypeIds: string[] = [],
  availableDetectionTypeIds?: string[]
): string[] => {
  if (!selectedDetectionTypeIds.length) {
    return selectedDetectionTypeIds;
  }

  if (!availableDetectionTypeIds) {
    return selectedDetectionTypeIds;
  }

  const availableIds = new Set(availableDetectionTypeIds);

  return selectedDetectionTypeIds.filter(id => availableIds.has(id));
};

export const buildPictureInferenceFilterOptionGroups = (
  pictureInferenceFilterOptions?: PictureInferenceFilterOptionsValue | null
): PictureInferenceFilterOptionGroup[] => {
  if (!pictureInferenceFilterOptions) {
    return [];
  }

  const groups: PictureInferenceFilterOptionGroup[] = [];

  pictureInferenceFilterOptionGroupKeys.forEach(groupKey => {
    const group = pictureInferenceFilterOptions[groupKey] as FilterOptionsGroupValue;
    const optionGroup = buildPictureInferenceFilterOptionGroup(groupKey, group);

    if (optionGroup) {
      groups.push(optionGroup);
    }
  });

  return groups;
};

export const buildPictureInferenceFilterSelectOptions = (
  pictureInferenceFilterOptions?: PictureInferenceFilterOptionsValue | null
): PictureInferenceFilterSelectOptionGroup[] => {
  const groups = buildPictureInferenceFilterOptionGroups(pictureInferenceFilterOptions);

  if (!groups.length) {
    return [];
  }

  return groups.map(group => ({
    label: group.inferenceApplication,
    options: group.options.map(option => ({
      ...option,
      searchText: option.label,
    })),
  }));
};

export const buildPictureInferenceFilterInput = (
  selectedDetectionTypeIds: string[] = [],
  deltaTMin?: number
): AtlasGqlPictureInferenceFilterInput | undefined => {
  const normalizedDeltaTMin =
    !hasDeltaTIncompatibleDetectionType(selectedDetectionTypeIds) && Number.isFinite(deltaTMin)
      ? deltaTMin
      : undefined;

  if (!selectedDetectionTypeIds.length && normalizedDeltaTMin === undefined) {
    return undefined;
  }

  return {
    panelDetectionMeta: {
      ...(selectedDetectionTypeIds.length ? { detectionTypes: selectedDetectionTypeIds } : {}),
      ...(normalizedDeltaTMin === undefined
        ? {}
        : {
            deltaTRange: {
              min: normalizedDeltaTMin,
            },
          }),
    },
  };
};
